import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { fetchFeatureClients, searchFeaturesByName } from "../api/featuresApi";
import { fetchAllLiveOrgs } from "../api/usersApi";
import { getClientDisplayName } from "../utils/clientDisplayName";
import "./FeatureAdoptionView.css";

/* ─── Helpers ────────────────────────────────────────────────── */

function formatDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-IN", {
        year: "numeric", month: "short", day: "numeric",
    });
}

function featureIdToLabel(id) {
    return (id || "")
        .replace(/^FEATURE_/, "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function AdoptionStatusChip({ status }) {
    const s = (status || "").toLowerCase();
    const cls =
        s === "released" ? "fav-status fav-status--released"
            : s === "ready" ? "fav-status fav-status--ready"
                : "fav-status fav-status--draft";
    return <span className={cls}>{status || "—"}</span>;
}

/* ─── Main Component ─────────────────────────────────────────── */

const FeatureAdoptionView = () => {
    // ── Search input state (shown in hero, before results)
    const [searchTerm, setSearchTerm] = useState("");

    // ── Autocomplete dropdown state
    const [dropdownItems, setDropdownItems] = useState([]);
    const [dropdownLoading, setDropdownLoading] = useState(false);
    const [dropdownError, setDropdownError] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    // ── Selected feature object after user clicks a dropdown item
    const [selectedFeature, setSelectedFeature] = useState(null);

    // ── Adoption results state (API 8)
    const [queriedId, setQueriedId] = useState(null);
    const [allItems, setAllItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // ── Filters (client-side)
    const [filterClient, setFilterClient] = useState("");      // free-text for client name

    // ── Pagination (client-side over filtered results)
    const PAGE_SIZE_OPTIONS = [10, 20, 50];
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [liveClientDisplayMap, setLiveClientDisplayMap] = useState(() => new Map());
    const [orgResolveError, setOrgResolveError] = useState("");

    const inputRef = useRef(null);
    const wrapRef = useRef(null);
    const debounceRef = useRef(null);
    const liveMapFetchedRef = useRef(false);
    const requestIdRef = useRef(0);
    const highlightedOptionRef = useRef(null);

    const loginDetails = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("loginDetails") || "null");
        } catch {
            return null;
        }
    }, []);

    /* ── Click-outside: close dropdown ──────────────────────── */
    useEffect(() => {
        function handleClickOutside(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    /* ── Live users fallback: resolve clientId → display name when not in loginDetails.clientData ── */
    useEffect(() => {
        if (!allItems.length || !queriedId) {
            liveMapFetchedRef.current = false;
            return;
        }
        if (liveMapFetchedRef.current) return;
        liveMapFetchedRef.current = true;
        setOrgResolveError("");
        fetchAllLiveOrgs()
            .then((clients) => {
                const map = new Map();
                for (const c of clients || []) {
                    if (c.clientId) {
                        const name = (c.displayName || c.org || "").trim();
                        if (name) map.set(c.clientId, name);
                    }
                }
                setLiveClientDisplayMap(map);
            })
            .catch((err) => {
                console.error("[FeatureAdoptionView] Failed to load live orgs for display names:", err);
                setOrgResolveError("Client names may be incomplete. Refresh to retry.");
                liveMapFetchedRef.current = false;
            });
    }, [allItems.length, queriedId]);

    /* ── Debounced name search (300ms) ──────────────────────── */
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        const term = searchTerm.trim();
        if (term.length < 2) {
            setDropdownItems([]);
            setDropdownOpen(false);
            setDropdownError("");
            return;
        }

        const requestId = ++requestIdRef.current;
        debounceRef.current = setTimeout(async () => {
            setDropdownLoading(true);
            setDropdownError("");
            try {
                const res = await searchFeaturesByName(term);
                if (requestId !== requestIdRef.current) return;
                const items = res?.data?.features ?? res?.features ?? [];
                setDropdownItems(items);
                setDropdownOpen(true);
            } catch (err) {
                if (requestId !== requestIdRef.current) return;
                setDropdownError(err?.message || "Search failed.");
                setDropdownItems([]);
                setDropdownOpen(true);
            } finally {
                if (requestId === requestIdRef.current) setDropdownLoading(false);
            }
        }, 300);

        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [searchTerm]);

    /* ── Fetch ALL adoption clients for chosen featureId (API 8) — no per-page limit ── */
    const MAX_FEATURE_CLIENTS = 500;
    const triggerAdoptionSearch = useCallback(async (featureId) => {
        if (!featureId) return;
        setLoading(true);
        setError("");
        setAllItems([]);
        setQueriedId(null);
        liveMapFetchedRef.current = false;
        setFilterClient("");
        try {
            let all = [];
            let cursor;
            const limit = 50;
            do {
                const res = await fetchFeatureClients(featureId, limit, cursor);
                const items = res?.data || [];
                all = [...all, ...items];
                cursor = res?.meta?.cursor || null;
            } while (cursor && all.length < MAX_FEATURE_CLIENTS);
            setQueriedId(featureId);
            setAllItems(all);
        } catch (err) {
            setError(err?.message || "Failed to retrieve feature adoption data.");
        } finally {
            setLoading(false);
        }
    }, []);

    /* ── User picks a feature from dropdown ────────────────── */
    const handleSelectFeature = useCallback((feature) => {
        setSelectedFeature(feature);
        setSearchTerm(feature.featureName || feature.featureId);
        // Close + clear dropdown immediately — results panel will take over
        setDropdownOpen(false);
        setDropdownItems([]);
        triggerAdoptionSearch(feature.featureId);
    }, [triggerAdoptionSearch]);

    /* ── Reset everything → back to hero search ────────────── */
    const handleClear = useCallback(() => {
        setSearchTerm("");
        setDropdownItems([]);
        setDropdownOpen(false);
        setDropdownError("");
        setSelectedFeature(null);
        setQueriedId(null);
        setAllItems([]);
        setError("");
        setFilterClient("");
        setCurrentPage(1);
        liveMapFetchedRef.current = false;
        setOrgResolveError("");
        // Focus input after a tick so hero has re-mounted
        setTimeout(() => inputRef.current?.focus(), 50);
    }, []);

    /* ── Keyboard ───────────────────────────────────────────── */
    /* ── Sync highlighted index when dropdown or items change ── */
    useEffect(() => {
        if (!dropdownOpen) {
            setHighlightedIndex(-1);
            return;
        }
        if (!dropdownError && dropdownItems.length > 0) {
            setHighlightedIndex((prev) => (prev < 0 || prev >= dropdownItems.length ? 0 : prev));
        } else {
            setHighlightedIndex(-1);
        }
    }, [dropdownOpen, dropdownError, dropdownItems.length]);

    useEffect(() => {
        highlightedOptionRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, [highlightedIndex]);

    const handleKeyDown = useCallback((e) => {
        const selectableCount = !dropdownError ? dropdownItems.length : 0;
        if (e.key === "Escape") {
            setDropdownOpen(false);
            setHighlightedIndex(-1);
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            if (selectableCount === 0) return;
            setHighlightedIndex((prev) => (prev + 1) % selectableCount);
            return;
        }
        if (e.key === "ArrowUp") {
            e.preventDefault();
            if (selectableCount === 0) return;
            setHighlightedIndex((prev) => (prev - 1 + selectableCount) % selectableCount);
            return;
        }
        if (e.key === "Enter") {
            e.preventDefault();
            if (selectableCount > 0 && highlightedIndex >= 0 && highlightedIndex < dropdownItems.length) {
                const feature = dropdownItems[highlightedIndex];
                if (feature) {
                    handleSelectFeature(feature);
                    setDropdownOpen(false);
                    setHighlightedIndex(-1);
                }
            }
        }
    }, [dropdownError, dropdownItems, highlightedIndex, handleSelectFeature]);

    /* Resolve client display name: loginDetails first, then live users API fallback (e.g. when client not in clientData). */
    const resolveClientDisplayName = useCallback((clientId) => {
        const fromLogin = getClientDisplayName(loginDetails, clientId);
        if (fromLogin && fromLogin !== clientId) return fromLogin;
        return liveClientDisplayMap.get(clientId) || clientId;
    }, [loginDetails, liveClientDisplayMap]);

    /* ── Client-side filtering (by clientId and display name) ── */
    const filteredItems = useMemo(() => {
        if (!filterClient.trim()) return allItems;
        const q = filterClient.toLowerCase();
        return allItems.filter((item) => {
            const id = (item.clientId || "").toLowerCase();
            const name = resolveClientDisplayName(item.clientId).toLowerCase();
            return id.includes(q) || name.includes(q);
        });
    }, [allItems, filterClient, resolveClientDisplayName]);

    /* ── Reset to page 1 when filter or feature changes ─────── */
    useEffect(() => {
        setCurrentPage(1);
    }, [filterClient, queriedId]);

    /* ── Pagination slice ───────────────────────────────────── */
    const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
    const paginatedItems = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredItems.slice(start, start + pageSize);
    }, [filteredItems, currentPage, pageSize]);

    // Determine which mode we're in
    const showResults = queriedId !== null && !loading;
    const showHero = !showResults && !loading;

    /* ── Render ─────────────────────────────────────────────── */
    return (
        <div className="fav">

            {/* ══════════════════════════════════════════════════════
                MODE A: Hero search (before any selection)
            ══════════════════════════════════════════════════════ */}
            {showHero && (
                <div className="fav-hero">
                    <div className="fav-hero__glow" aria-hidden="true" />
                    <div className="fav-hero__inner">
                        <div className="fav-hero__badge" aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                        </div>
                        <h2 className="fav-hero__title">Feature Adoption Tracker</h2>
                        <p className="fav-hero__desc">
                            Search by feature name to see which clients have received it,
                            their adoption status, and release dates.
                        </p>

                        {/* Input + dropdown wrapper */}
                        <div className="fav-search-form" ref={wrapRef}>
                            <div className="fav-search-wrap">
                                <svg className="fav-search-icon" width="18" height="18" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <circle cx="11" cy="11" r="8" />
                                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>

                                <input
                                    ref={inputRef}
                                    type="text"
                                    className="fav-search-input"
                                    placeholder="Search features by name, e.g. dark mode"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onFocus={() => {
                                        // Re-open only if no feature selected yet and items available
                                        if (!selectedFeature && dropdownItems.length > 0) setDropdownOpen(true);
                                    }}
                                    onKeyDown={handleKeyDown}
                                    aria-label="Search features by name"
                                    aria-autocomplete="list"
                                    aria-expanded={dropdownOpen}
                                    aria-activedescendant={dropdownOpen && highlightedIndex >= 0 && !dropdownError && dropdownItems.length > 0 ? `fav-option-${highlightedIndex}` : undefined}
                                    autoComplete="off"
                                    spellCheck={false}
                                />

                                {/* Inline spinner */}
                                {dropdownLoading && (
                                    <span className="fav-search-spinner" aria-label="Searching…">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" strokeWidth="2"
                                            strokeLinecap="round" strokeLinejoin="round"
                                            style={{ animation: "fav-spin 0.8s linear infinite" }}
                                            aria-hidden="true">
                                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                        </svg>
                                    </span>
                                )}

                                {/* Clear button */}
                                {searchTerm && !dropdownLoading && (
                                    <button
                                        type="button"
                                        className="fav-search-clear"
                                        onClick={handleClear}
                                        aria-label="Clear search"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" strokeWidth="2.5"
                                            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <line x1="18" y1="6" x2="6" y2="18" />
                                            <line x1="6" y1="6" x2="18" y2="18" />
                                        </svg>
                                    </button>
                                )}

                                {/* Autocomplete dropdown */}
                                {dropdownOpen && (
                                    <div className="fav-autocomplete" role="listbox" aria-label="Feature suggestions">
                                        {dropdownError && (
                                            <div className="fav-autocomplete__item fav-autocomplete__item--message">
                                                <span style={{ color: "#b91c1c", fontSize: "0.8125rem" }}>
                                                    ⚠ {dropdownError}
                                                </span>
                                            </div>
                                        )}
                                        {!dropdownError && dropdownItems.length === 0 && !dropdownLoading && (
                                            <div className="fav-autocomplete__item fav-autocomplete__item--message">
                                                <span style={{ color: "#94a3b8", fontSize: "0.8125rem" }}>
                                                    No features found for "{searchTerm}"
                                                </span>
                                            </div>
                                        )}
                                        {!dropdownError && dropdownItems.map((feature, idx) => (
                                            <button
                                                key={feature.featureId || idx}
                                                id={`fav-option-${idx}`}
                                                type="button"
                                                ref={idx === highlightedIndex ? highlightedOptionRef : undefined}
                                                className={`fav-autocomplete__item${idx === highlightedIndex ? " fav-autocomplete__item--highlighted" : ""}`}
                                                role="option"
                                                aria-selected={idx === highlightedIndex}
                                                onClick={() => handleSelectFeature(feature)}
                                            >
                                                <div className="fav-autocomplete__name">
                                                    {feature.featureName || featureIdToLabel(feature.featureId)}
                                                </div>
                                                <div className="fav-autocomplete__meta">
                                                    <span className="fav-autocomplete__id">{feature.featureId}</span>
                                                    {feature.releaseType && (
                                                        <span className={`fav-autocomplete__type ${feature.releaseType === "GENERIC"
                                                            ? "fav-autocomplete__type--generic"
                                                            : "fav-autocomplete__type--client"}`}>
                                                            {feature.releaseType}
                                                        </span>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="fav-registry-hint">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="16" x2="12" y2="12" />
                                <line x1="12" y1="8" x2="12.01" y2="8" />
                            </svg>
                            Type at least 2 characters — results appear as you type
                        </div>
                    </div>
                </div>
            )}

            {/* ── Static info cards (hero only) ─────────────────────────────── */}
            {showHero && (
                <div className="fav-cards" aria-label="How this tracker helps you">
                    <div className="fav-info-card">
                        <div className="fav-info-card__icon" aria-hidden="true">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                                <polyline points="10 9 9 9 8 9" />
                            </svg>
                        </div>
                        <h3 className="fav-info-card__title">Track by feature</h3>
                        <p className="fav-info-card__desc">
                            Search any feature by name to see which clients have received it, with release date and status (Draft, Ready, Released).
                        </p>
                    </div>
                    <div className="fav-info-card">
                        <div className="fav-info-card__icon fav-info-card__icon--filter" aria-hidden="true">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                            </svg>
                        </div>
                        <h3 className="fav-info-card__title">Filter & refine</h3>
                        <p className="fav-info-card__desc">
                            After selecting a feature, filter the client list by name. Use per-page and pagination to browse large result sets.
                        </p>
                    </div>
                    <div className="fav-info-card">
                        <div className="fav-info-card__icon fav-info-card__icon--pipeline" aria-hidden="true">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                            </svg>
                        </div>
                        <h3 className="fav-info-card__title">Stays in sync</h3>
                        <p className="fav-info-card__desc">
                            Create and execute releases in the <strong>All Releases</strong> tab; adoption data here reflects your pipeline in real time.
                        </p>
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════════════════
                Loading spinner (between select and results render)
            ══════════════════════════════════════════════════════ */}
            {loading && (
                <div className="fav-loading-wrap" aria-label="Loading adoption data">
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
                        stroke="#3567d6" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round"
                        style={{ animation: "fav-spin 1s linear infinite" }}
                        aria-hidden="true">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    <span>Loading adoption data…</span>
                </div>
            )}

            {/* ── Error banner ─────────────────────────────────── */}
            {error && (
                <div className="fav-error" role="alert">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span><strong>Error:</strong> {error}</span>
                </div>
            )}

            {/* ── Org resolution warning (live clients fetch failed) ── */}
            {orgResolveError && (
                <div className="fav-org-resolve-warning" role="status">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>{orgResolveError}</span>
                </div>
            )}

            {/* ══════════════════════════════════════════════════════
                MODE B: Results (compact header + filter bar + table)
            ══════════════════════════════════════════════════════ */}
            {showResults && !error && (
                <div className="fav-results" style={{ animation: "fav-fade-up 0.25s ease forwards" }}>

                    {/* ── Compact active-search header ─────────────── */}
                    <div className="fav-active-search">
                        <div className="fav-active-search__left">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            <span className="fav-active-search__name">
                                {selectedFeature?.featureName || featureIdToLabel(queriedId)}
                            </span>
                            <code className="fav-active-search__id">{queriedId}</code>
                            {selectedFeature?.releaseType && (
                                <span className={`fav-autocomplete__type ${selectedFeature.releaseType === "GENERIC"
                                    ? "fav-autocomplete__type--generic"
                                    : "fav-autocomplete__type--client"}`}>
                                    {selectedFeature.releaseType}
                                </span>
                            )}
                        </div>
                        <div className="fav-active-search__right">
                            <div className="fav-results__count-badge fav-results__count-badge--sm">
                                <span className="fav-results__count-num">{allItems.length}</span>
                                <span className="fav-results__count-label">{allItems.length === 1 ? "client" : "clients"}</span>
                            </div>
                            <button
                                type="button"
                                className="fav-active-search__reset"
                                onClick={handleClear}
                                aria-label="Search again"
                            >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2.5"
                                    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                                Search again
                            </button>
                        </div>
                    </div>

                    {/* ── Filter bar — Client name search only ─── */}
                    <div className="fav-filter-bar">
                        <div className="fav-filter-group fav-filter-group--search">
                            <label className="fav-filter-label" htmlFor="fav-filter-client">Filter by Client Name</label>
                            <div className="fav-filter-search-wrap">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round"
                                    className="fav-filter-search-icon" aria-hidden="true">
                                    <circle cx="11" cy="11" r="8" />
                                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                                <input
                                    id="fav-filter-client"
                                    type="text"
                                    className="fav-filter-input"
                                    placeholder="Search client name…"
                                    value={filterClient}
                                    onChange={(e) => setFilterClient(e.target.value)}
                                />
                                {filterClient && (
                                    <button
                                        type="button"
                                        className="fav-filter-clear"
                                        onClick={() => setFilterClient("")}
                                        aria-label="Clear client filter"
                                    >
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" strokeWidth="2.5"
                                            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <line x1="18" y1="6" x2="6" y2="18" />
                                            <line x1="6" y1="6" x2="18" y2="18" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Active count */}
                        {filterClient && (
                            <span className="fav-filter-count" style={{ alignSelf: "flex-end", paddingBottom: "7px" }}>
                                {filteredItems.length} of {allItems.length} shown
                            </span>
                        )}
                    </div>

                    {/* ── Empty state (no results after filter) ────── */}
                    {filteredItems.length === 0 ? (
                        <div className="fav-empty">
                            <div className="fav-empty__icon" aria-hidden="true">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="1.5"
                                    strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8" />
                                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                    <line x1="8" y1="11" x2="14" y2="11" />
                                </svg>
                            </div>
                            <h4 className="fav-empty__title">
                                {allItems.length === 0 ? "No adoption data found" : "No results match the filters"}
                            </h4>
                            <p className="fav-empty__desc">
                                {allItems.length === 0
                                    ? <>No clients have received <strong>{queriedId}</strong> yet.</>
                                    : "Try adjusting or clearing the active filters."}
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Results table */}
                            <div className="fav-table-wrap">
                                <table className="fav-table" aria-label={`Clients who adopted ${queriedId}`}>
                                    <thead>
                                        <tr className="fav-table__head-row">
                                            <th className="fav-table__th">Client Name</th>
                                            <th className="fav-table__th">Feature</th>
                                            <th className="fav-table__th">Release Date</th>
                                            <th className="fav-table__th">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedItems.map((item, idx) => (
                                            <tr key={`${item.clientId}-${item.featureId}-${idx}`} className="fav-table__row">
                                                <td className="fav-table__td">
                                                    <span className="fav-client-id-badge">
                                                        {resolveClientDisplayName(item.clientId)}
                                                    </span>
                                                </td>
                                                <td className="fav-table__td">
                                                    <span className="fav-feature-name">{featureIdToLabel(item.featureId)}</span>
                                                    <span className="fav-feature-id">{item.featureId}</span>
                                                </td>
                                                <td className="fav-table__td fav-table__td--date">
                                                    {formatDate(item.releaseDate)}
                                                </td>
                                                <td className="fav-table__td">
                                                    <AdoptionStatusChip status={item.status} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="fav-pagination">
                                <div className="fav-pagination__left">
                                    <span className="fav-pagination__info">
                                        {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredItems.length)} of {filteredItems.length}
                                    </span>
                                    <span className="fav-pagination__sep" aria-hidden="true">·</span>
                                    <label className="fav-pagination__per-page">
                                        <span className="fav-pagination__per-page-label">Per page</span>
                                        <select
                                            className="fav-pagination__per-page-select"
                                            value={pageSize}
                                            onChange={(e) => {
                                                setPageSize(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            aria-label="Results per page"
                                        >
                                            {PAGE_SIZE_OPTIONS.map((n) => (
                                                <option key={n} value={n}>{n}</option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                                <div className="fav-pagination__controls">
                                    <button
                                        type="button"
                                        className="fav-pagination__btn fav-pagination__btn--nav"
                                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        aria-label="Previous page"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" strokeWidth="2.5"
                                            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <path d="m15 18-6-6 6-6" />
                                        </svg>
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                                        .reduce((acc, p, idx, arr) => {
                                            if (idx > 0 && p - arr[idx - 1] > 1) acc.push("ellipsis-" + p);
                                            acc.push(p);
                                            return acc;
                                        }, [])
                                        .map((item) =>
                                            typeof item === "string" ? (
                                                <span key={item} className="fav-pagination__ellipsis">…</span>
                                            ) : (
                                                <button
                                                    key={item}
                                                    type="button"
                                                    className={`fav-pagination__btn ${item === currentPage ? "fav-pagination__btn--active" : ""}`}
                                                    onClick={() => setCurrentPage(item)}
                                                    aria-current={item === currentPage ? "page" : undefined}
                                                >
                                                    {item}
                                                </button>
                                            )
                                        )}
                                    <button
                                        type="button"
                                        className="fav-pagination__btn fav-pagination__btn--nav"
                                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        aria-label="Next page"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" strokeWidth="2.5"
                                            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <path d="m9 18 6-6-6-6" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

        </div>
    );
};

export default FeatureAdoptionView;
